import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { AddToProject } from "../components/ProjectDialogs";
import { RenameMeeting } from "../components/RenameMeeting";
import { RowMenu } from "../components/RowMenu";
import { endpoints, type Meeting } from "../lib/api";
import { PLATFORM_NAMES, meetingPlatformKey, platformKey, sameDay, timeOf, type PlatformKey } from "../lib/calendar";
import { duration } from "../lib/format";

const PAGE = 25;

type Tab = "past" | "upcoming";
// "in_person": recorded on the Record page — no link, so no platform of its own.
// "uploaded": from the Upload page, whatever app it was recorded in.
type PlatformFilter = "all" | PlatformKey | "in_person" | "uploaded";
type StatusFilter = "all" | "ready" | "processing" | "missed";

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "All",
  ready: "Ready",
  processing: "Processing",
  missed: "Didn't record",
};

/** Every meeting you were part of, past and coming up: searchable, filtered
 * by platform and outcome, grouped by day. */
export function Meetings() {
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  const { data, error, isLoading } = useQuery({
    queryKey: ["recordings"],
    queryFn: endpoints.recordings,
    refetchInterval: 30_000,
  });

  const [tab, setTab] = useState<Tab>("past");
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [mine, setMine] = useState(false);
  // A project's id, or "all".
  const [project, setProject] = useState("all");
  const projects = useQuery({ queryKey: ["projects"], queryFn: endpoints.projects });
  const [shown, setShown] = useState(PAGE);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = [...(data?.[tab] ?? [])];
    // Upcoming reads soonest first; past, newest first.
    list.sort((a, b) => {
      const diff = when(a).getTime() - when(b).getTime();
      return tab === "upcoming" ? diff : -diff;
    });
    return list.filter((meeting) => {
      if (platform === "in_person" ? !meeting.in_person
          : platform === "uploaded" ? !meeting.uploaded
          : platform !== "all" && meetingPlatformKey(meeting) !== platform) return false;
      if (tab === "past" && status !== "all" && outcome(meeting) !== status) return false;
      if (mine && me.data && meeting.owner_email?.toLowerCase() !== me.data.email.toLowerCase()) return false;
      if (project !== "all" && !(meeting.projects ?? []).some((p) => String(p.id) === project)) return false;
      if (!needle) return true;
      return [meeting.title, meeting.meet_url, meeting.owner_email].some((text) => text?.toLowerCase().includes(needle));
    });
  }, [data, tab, query, platform, status, mine, me.data, project]);

  const groups = groupByDay(rows.slice(0, shown));
  const filtered = query.trim() || platform !== "all" || (tab === "past" && status !== "all") || mine
    || project !== "all";

  function reset<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value);
      setShown(PAGE);
    };
  }

  return (
    <div className="page-body meetings-page">
      <header className="meetings-head">
        <div>
          <h1>Meetings</h1>
          <p className="meetings-sub">
            {data ? `${data.past.length} past · ${data.upcoming.length} coming up` : "Loading…"}
            {me.data?.sees_all && " · as an admin you see everyone's"}
          </p>
        </div>
        <div className="segmented" role="tablist" aria-label="Past or upcoming">
          {(["past", "upcoming"] as Tab[]).map((name) => (
            <button key={name} role="tab" aria-selected={tab === name} className={tab === name ? "on" : ""}
                    onClick={() => reset(setTab)(name)}>
              {name === "past" ? "Past" : "Upcoming"}
              <span className="seg-count">{data?.[name]?.length ?? 0}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="meetings-tools">
        <input
          className="meetings-search"
          type="search"
          placeholder="Search by title, link or who sent the bot"
          value={query}
          onChange={(event) => reset(setQuery)(event.target.value)}
          aria-label="Search meetings"
        />
        <div className="filter" role="group" aria-label="Platform">
          {(["all", "meet", "teams", "zoom", "in_person", "uploaded"] as PlatformFilter[]).map((key) => (
            <button key={key} className={`chip-button${platform === key ? " on" : ""}${key !== "all" ? ` p-${key}` : ""}`}
                    onClick={() => reset(setPlatform)(key)}>
              {key !== "all" && key !== "uploaded" && <span className="mark" aria-hidden />}
              {key === "all" ? "All platforms" : key === "in_person" ? "In person" : key === "uploaded" ? "Uploaded"
                : PLATFORM_NAMES[key]}
            </button>
          ))}
        </div>
        {tab === "past" && (
          <div className="filter" role="group" aria-label="Status">
            {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((key) => (
              <button key={key} className={`chip-button${status === key ? " on" : ""}`} onClick={() => reset(setStatus)(key)}>
                {STATUS_LABELS[key]}
              </button>
            ))}
          </div>
        )}
        {(projects.data ?? []).length > 0 && (
          <select className="project-filter" value={project} onChange={(event) => reset(setProject)(event.target.value)}
                  aria-label="Project">
            <option value="all">All projects</option>
            {(projects.data ?? []).map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        )}
        <label className="mine">
          <input type="checkbox" checked={mine} onChange={(event) => reset(setMine)(event.target.checked)} />
          Mine only
        </label>
      </div>

      {error && <div className="error">{(error as Error).message}</div>}

      <section className="card meetings-card">
        {isLoading ? (
          <p className="dash-empty">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="dash-empty">
            {filtered
              ? "No meetings match. Try another search or filter."
              : tab === "past"
                ? "No meetings yet."
                : "Nothing coming up. Meetings with a Meet or Teams link appear here from your calendar."}
          </p>
        ) : (
          groups.map(({ day, meetings }) => (
            <div key={day.toDateString()} className="day-group">
              <h2 className="day-group-head">
                {dayHeading(day)}
                <span>{meetings.length}</span>
              </h2>
              {meetings.map((meeting) =>
                tab === "past" ? (
                  <PastRow key={rowKey(meeting)} meeting={meeting} csrfToken={me.data?.csrf_token ?? ""} />
                ) : (
                  <UpcomingRow key={rowKey(meeting)} meeting={meeting} />
                ),
              )}
            </div>
          ))
        )}
        {rows.length > shown && (
          <div className="meetings-more">
            <span>
              Showing {shown} of {rows.length}
            </span>
            <button className="btn-secondary" onClick={() => setShown(shown + PAGE)}>Show more</button>
          </div>
        )}
      </section>
    </div>
  );
}

function PastRow({ meeting, csrfToken }: { meeting: Meeting; csrfToken: string }) {
  const [filing, setFiling] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const key = meetingPlatformKey(meeting);
  const start = when(meeting);
  const cells = (
    <>
      <span className="m-time">{start.getTime() ? timeOf(start) : "—"}</span>
      <span className="m-main">
        {renaming && meeting.session_id ? (
          <RenameMeeting sessionId={meeting.session_id} title={meeting.title ?? ""} csrfToken={csrfToken}
                         onDone={() => setRenaming(false)} />
        ) : (
          <span className="dash-title">{meeting.title ?? meeting.meet_url ?? meeting.session_id ?? "Untitled meeting"}</span>
        )}
        <span className="m-meta">
          {key ? <span className="event-platform">{PLATFORM_NAMES[key]}</span>
            : meeting.in_person && <span className="event-platform">In person</span>}
          {meeting.uploaded && <span className="upload-tag">Uploaded</span>}
          {meeting.owner_email && <span>by {meeting.owner_email}</span>}
          {(meeting.projects ?? []).map((p) => <span key={p.id} className="m-project">{p.name}</span>)}
        </span>
      </span>
      <span className="dash-num">{meeting.duration_seconds ? duration(meeting.duration_seconds) : "—"}</span>
      <Outcome meeting={meeting} />
    </>
  );
  const className = `m-row p-${key ?? (meeting.in_person ? "in_person" : "none")}`;
  // No session id means no bot ever ran, so there's nothing to open or file.
  if (!meeting.session_id) {
    return <div className={className}>{cells}<span /></div>;
  }
  // The whole row opens the meeting (a link stretched over it); the ⋯ sits
  // above that link, so it isn't a button inside a link.
  return (
    <div className={`${className} linked`}>
      <Link className="m-row-link" to={`/meetings/${meeting.session_id}`}
            aria-label={meeting.title ?? "Open meeting"} />
      {cells}
      <RowMenu items={[
        ...(meeting.can_rename ? [{ label: "Rename", onClick: () => setRenaming(true) }] : []),
        { label: "Add to project", onClick: () => setFiling(true) },
      ]} />
      {filing && (
        <AddToProject
          sessionId={meeting.session_id}
          title={meeting.title ?? "Untitled meeting"}
          inProjects={meeting.projects ?? []}
          csrfToken={csrfToken}
          onClose={() => setFiling(false)}
        />
      )}
    </div>
  );
}

function UpcomingRow({ meeting }: { meeting: Meeting }) {
  const key = platformKey(meeting.meet_url);
  const start = when(meeting);
  const end = meeting.ends_at ? new Date(meeting.ends_at) : null;
  const off = meeting.state === "cancelled";
  return (
    <div className={`m-row upcoming p-${key ?? (meeting.in_person ? "in_person" : "none")}`}>
      <span className="m-time">
        {timeOf(start)}
        {end && <span className="m-end">–{timeOf(end)}</span>}
      </span>
      <span className="m-main">
        <span className="dash-title">{meeting.title ?? "Untitled meeting"}</span>
        <span className="m-meta">
          {key ? <span className="event-platform">{PLATFORM_NAMES[key]}</span>
            : meeting.in_person && <span className="event-platform">In person</span>}
          {meeting.meet_url && (
            <a href={meeting.meet_url} target="_blank" rel="noreferrer">Meeting link ↗</a>
          )}
        </span>
      </span>
      <span />
      <span className={`dash-tag-lite${off ? " off" : ""}`}>{off ? "Not recording" : "Bot joins"}</span>
      <span />
    </div>
  );
}

/** Ready, on its way, or why there's nothing. */
function Outcome({ meeting }: { meeting: Meeting }) {
  switch (outcome(meeting)) {
    case "missed":
      return <span className="status failed">{meeting.label}</span>;
    case "ready":
      return <span className="status ready">Ready</span>;
    case "processing":
      return (
        <span className="status waiting">
          <span className="status-dot" aria-hidden />
          {meeting.active ? meeting.label : "Transcribing…"}
        </span>
      );
    default:
      return <span className="status">{meeting.state === "cancelled" ? "Cancelled" : "No transcript"}</span>;
  }
}

function outcome(meeting: Meeting): StatusFilter | "other" {
  if (meeting.state === "failed" || meeting.state === "not_admitted") return "missed";
  if (meeting.transcript === "ready") return "ready";
  if (meeting.active || meeting.transcript === "pending") return "processing";
  return "other";
}

function when(meeting: Meeting): Date {
  return new Date(meeting.starts_at ?? meeting.requested_at ?? 0);
}

function rowKey(meeting: Meeting): string {
  return meeting.session_id ?? `${meeting.meet_url}-${meeting.starts_at}`;
}

function groupByDay(meetings: Meeting[]): { day: Date; meetings: Meeting[] }[] {
  const groups: { day: Date; meetings: Meeting[] }[] = [];
  for (const meeting of meetings) {
    const start = when(meeting);
    const last = groups[groups.length - 1];
    if (last && sameDay(last.day, start)) last.meetings.push(meeting);
    else groups.push({ day: start, meetings: [meeting] });
  }
  return groups;
}

/** Today, Yesterday, Tomorrow, or "Mon 28 Sep" (with the year when it isn't this one). */
function dayHeading(day: Date): string {
  if (!day.getTime()) return "No date";
  const today = new Date();
  const offset = (n: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + n);
  if (sameDay(day, today)) return "Today";
  if (sameDay(day, offset(-1))) return "Yesterday";
  if (sameDay(day, offset(1))) return "Tomorrow";
  return day.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(day.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}
